import Cookies from 'js-cookie';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { getCityzensProfile } from 'services/account/get/get-cityzens-profile.api';
import type { GetCityzensProfileResponse } from 'services/account/get/types/get-cityzens-profile.type';

let refreshInFlight: Promise<void> | null = null;

export type UserProps = GetCityzensProfileResponse & {
	isLoggedIn?: boolean;
};

type UserState = {
	user: UserProps | null;
	lastUserFetchedAt: number | null;
};

type UserActions = {
	setUser: (user: UserProps) => void;
	clearUser: () => void;
	refreshUser: () => Promise<void>;
};

type UseUserStoreProps = UserState & UserActions;

export const useUserStore = create<UseUserStoreProps>()(
	persist<UseUserStoreProps>(
		(set, get) => ({
			user: null,
			lastUserFetchedAt: null,
			setUser: (user) => {
				const tempUser = { ...user, isLoggedIn: true };

				if (tempUser.avatar?.url) {
					tempUser.avatar.url += `?${new Date().getTime()}`;
				}

				set(() => ({ user: tempUser, lastUserFetchedAt: Date.now() }));
			},
			clearUser: () =>
				set(() => ({ user: null, lastUserFetchedAt: null })),
			refreshUser: async () => {
				// Skip profile refresh during build time (SSG/SSR)
				if (typeof window === 'undefined') {
					return;
				}

				if (refreshInFlight) return refreshInFlight;

				refreshInFlight = getCityzensProfile()
					.then((data) => {
						if (!data) {
							get().clearUser();
							return;
						}
						get().setUser(data);
					})
					.catch((error) => {
						console.error('Profile refresh failed:', error);
					})
					.finally(() => {
						refreshInFlight = null;
					});

				return refreshInFlight;
			},
		}),
		{
			name: 'cfg_user_store',
			storage: createJSONStorage(() => ({
				getItem: (key) => Cookies.get(key) ?? null,
				setItem: (key, value) =>
					Cookies.set(key, value, {
						expires: 3,
						secure: process.env.NODE_ENV === 'production',
						sameSite: 'Lax',
					}),
				removeItem: (key) => Cookies.remove(key),
			})),
			version: 1.2,
			onRehydrateStorage: () => {
				return (state, error) => {
					if (error) {
						console.log(
							'user store: error during hydration',
							error
						);
						return;
					}

					// Always revalidate profile after hydration so each hard reload fetches fresh user state.
					state?.refreshUser?.();
				};
			},
		}
	)
);
